import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DatePicker, Segmented, Tag } from "antd";
import s from "./Filters.module.scss";
import { FilterIcon } from "../../../../../../global components/Icons/FilterIcon";
import ReusablePopover from "../../../../../../global components/Popover/ReuseablePopover";
import FilterPopOverContent from "../FilterPopOverContent";
import { CloseIcon } from "../../../../../../global components/Icons/CloseIcon";
dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const dateFormat = "MMM DD, YYYY";
type PropType = {
  handleType: (type: string) => void;
  handleReportDate: (date: {
    startDate: string | undefined;
    endDate: string | undefined;
  }) => void;
  handleStatus: (status: "failed" | "delivered" | undefined) => void;
  handleSort: (sort: "asc" | "desc" | undefined) => void;
  handleUserType: (types: string[]) => void;
  userType: string[] | undefined;
  handleMessagingChannel: (channel: "sms" | "mms" | undefined) => void;
  handleComplianceStatus: (
    status: "spam" | "failed" | "revoked" | undefined,
  ) => void;
  handleVoiceChannel: (channel: "voice" | "voice_call" | undefined) => void;
  handleFinancialChannel: (
    channel: "sms" | "mms" | "email" | "voice" | undefined,
  ) => void;
  page: number;
  limit: number;
};
function Filters({
  handleType,
  handleReportDate,
  handleSort,
  handleStatus,
  handleUserType,
  userType,
  handleMessagingChannel,
  handleVoiceChannel,
  handleComplianceStatus,
  handleFinancialChannel,
  limit,
  page,
}: PropType) {
  const [date, setDate] = useState<{
    startDate: string | undefined;
    endDate: string | undefined;
  }>();
  const [type, setType] = useState<string>("user");
  const [order, setOrder] = useState<"asc" | "desc">();
  const [messagingChannel, setMessagingChannel] = useState<
    "sms" | "mms" | undefined
  >(undefined);
  const [voiceChannel, setVoiceChannel] = useState<
    "voice-broadcast" | "voice-message"
  >();
  const [user, setUser] = useState<{
    active: boolean;
    inActive: boolean;
    newSignUps: boolean;
    highUsageAccount: boolean;
  }>({
    active: true,
    inActive: false,
    newSignUps: true,
    highUsageAccount: false,
  });
  const [compliance, setCompliance] = useState<{
    spam: boolean;
    revoked: boolean;
    failed: boolean;
  }>({
    spam: false,
    revoked: false,
    failed: false,
  });
  const [financialChannel, setFinancialChannel] = useState<
    "sms" | "mms" | "email" | "voice"
  >();
  const [status, setStatus] = useState<"failed" | "delivered">();
  const handleDate = (dates: [Dayjs | null, Dayjs | null]) => {
    handleReportDate({
      endDate: dates[1]?.format("YYYY-MM-DD"),
      startDate: dates[0]?.format("YYYY-MM-DD"),
    });
    setDate({
      endDate: dates[1]?.format("YYYY-MM-DD"),
      startDate: dates[0]?.format("YYYY-MM-DD"),
    });
  };
  useEffect(() => {
    handleStatus(status);
    handleSort(order);
    handleUserType([
      ...(user.active ? ["active"] : []),
      ...(user.inActive ? ["inactive"] : []),
      ...(user.newSignUps ? ["new_signup"] : []),
      ...(user.highUsageAccount ? ["high_use_acc"] : []),
    ]);
    handleMessagingChannel(messagingChannel);
    handleFinancialChannel(financialChannel);
    handleVoiceChannel(
      voiceChannel === "voice-message" ? "voice" : "voice_call",
    );
    handleComplianceStatus(
      compliance.spam
        ? "spam"
        : compliance.revoked
          ? "revoked"
          : compliance.failed
            ? "failed"
            : undefined,
    );
  }, [
    compliance.failed,
    compliance.revoked,
    compliance.spam,
    financialChannel,
    handleComplianceStatus,
    handleFinancialChannel,
    handleMessagingChannel,
    handleSort,
    handleStatus,
    handleUserType,
    handleVoiceChannel,
    messagingChannel,
    order,
    status,
    user.active,
    user.highUsageAccount,
    user.inActive,
    user.newSignUps,
    voiceChannel,
  ]);
  useEffect(() => {
    setOrder(undefined);
    setFinancialChannel(undefined);
    if (type === "messaging") {
      setMessagingChannel(undefined);
    } else {
      setMessagingChannel(undefined);
    }
    setStatus(undefined);
    if (type === "voice") {
      setVoiceChannel("voice-broadcast");
    } else {
      setVoiceChannel(undefined);
    }
    if (type === "user") {
      setUser({
        active: false,
        highUsageAccount: false,
        inActive: false,
        newSignUps: false,
      });
    } else {
      setUser({
        active: false,
        highUsageAccount: false,
        inActive: false,
        newSignUps: false,
      });
    }
    if (type === "compliance") {
      setCompliance({
        failed: false,
        revoked: false,
        spam: false,
      });
    } else {
      setCompliance({
        failed: false,
        revoked: false,
        spam: false,
      });
    }
  }, [type]);
  return (
    <div className={s.mainWrapper}>
      <div className={s.wrapper}>
        <div className={s.sectionOne}>
          <RangePicker
            defaultValue={[
              dayjs().subtract(1, "month").startOf("day"), // today at midnight
              dayjs().endOf("day"), // today end of day
            ]}
            format={dateFormat}
            className={s.rangePicker}
            onChange={(dates, _) => {
              if (!dates) return; // handle case when cleared

              handleDate(dates);
            }}
          />
          <Segmented<string>
            options={[
              "User",
              "Messaging",
              "Email",
              "Voice",
              "Financial",
              "Compliance",
            ]}
            onChange={(value) => {
              setType(value.toLowerCase());
              handleType(value.toLowerCase());
            }}
            className="custom-segmented"
            //value={value}
          />
        </div>
        <div className={s.sectionTwo}>
          <ReusablePopover
            trigger={"hover"}
            content={
              <FilterPopOverContent
                financialChannel={financialChannel}
                messagingChannel={messagingChannel}
                status={status}
                voiceChannel={voiceChannel}
                order={order}
                user={user}
                setUser={setUser}
                compliance={compliance}
                setCompliance={setCompliance}
                setFinancialChannel={setFinancialChannel}
                setMessagingChannel={setMessagingChannel}
                setOrder={setOrder}
                setStatus={setStatus}
                setVoiceChannel={setVoiceChannel}
                reportType={type}
                key={type}
              />
            }
          >
            <button className={s.filter}>
              <FilterIcon className={s.filterIcon} />
              <p>Filter</p>
            </button>
          </ReusablePopover>
        </div>
      </div>
      <div className={s.tags}>
        {[
          { value: order, setter: () => setOrder(undefined) },
          {
            value: financialChannel,
            setter: () => setFinancialChannel(undefined),
          },
          {
            value: messagingChannel,
            setter: () => setMessagingChannel(undefined),
          },
          { value: status, setter: () => setStatus(undefined) },
          { value: voiceChannel, setter: () => setVoiceChannel(undefined) },
          ...(user.active
            ? [
                {
                  value: "active",
                  setter: () =>
                    setUser((prev) => ({
                      ...prev,
                      active: false,
                    })),
                },
              ]
            : []),
          ...(user.inActive
            ? [
                {
                  value: "inactive",
                  setter: () =>
                    setUser((prev) => ({
                      ...prev,
                      inActive: false,
                    })),
                },
              ]
            : []),
          ...(user.highUsageAccount
            ? [
                {
                  value: "high usage account",
                  setter: () =>
                    setUser((prev) => ({
                      ...prev,
                      highUsageAccount: false,
                    })),
                },
              ]
            : []),
          ...(user.newSignUps
            ? [
                {
                  value: "new signups",
                  setter: () =>
                    setUser((prev) => ({
                      ...prev,
                      newSignUps: false,
                    })),
                },
              ]
            : []),
          ...(compliance.spam
            ? [
                {
                  value: "spam",
                  setter: () =>
                    setCompliance((prev) => ({
                      ...prev,
                      spam: false,
                    })),
                },
              ]
            : []),
          ...(compliance.revoked
            ? [
                {
                  value: "revoked",
                  setter: () =>
                    setCompliance((prev) => ({
                      ...prev,
                      revoked: false,
                    })),
                },
              ]
            : []),
          ...(compliance.failed
            ? [
                {
                  value: "failed",
                  setter: () =>
                    setCompliance((prev) => ({
                      ...prev,
                      failed: false,
                    })),
                },
              ]
            : []),
        ]
          .filter(({ value }) => value)
          .map(({ value, setter }, idx) => (
            <Tag
              key={idx}
              closable
              onClose={setter}
              color="#FFF1EF"
              className={s.tag}
              closeIcon={
                <span className={s.iconWrapper}>
                  <CloseIcon width={5} height={5} color="#ffff" />
                </span>
              }
            >
              <p className={s.tagValue}>{value}</p>
            </Tag>
          ))}
      </div>
    </div>
  );
}

export default Filters;
